import React, { useEffect } from 'react';
import * as analyticsModule from '../services/analytics';

// OPERA L3 DISP-S1 product page (JPL / NASA)
const OPERA_LINK = 'https://www.jpl.nasa.gov/go/opera/products/disp-s1/';

// Tier badge styling — colorblind-distinguishable because each badge
// carries a text label, not color alone (AC-003-3 / WCAG 1.4.1).
const TIER_CONFIG = {
  stable: { label: 'Stable', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  watch:  { label: 'Watch',  bg: '#fff8e1', color: '#e65100', border: '#ffcc80' },
  alert:  { label: 'Alert',  bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
};

// Non-dismissible disclaimer required by RISK-010 / AC-003-2.
// Must appear whenever a tier (stable / watch / alert) is rendered;
// it is NOT hidden behind a hover-only tooltip.
const DISCLAIMER =
  'Directional signal — not a geotechnical survey. Values are relative ' +
  'to the surrounding area (frame-median-normalized via OPERA DISP-S1).';

/**
 * GroundStabilityCard
 *
 * Renders the OPP-3 ground-stability section of the parcel detail page.
 *
 * Props:
 *   groundStability – the geo_signals.ground_stability object from
 *                     GET /api/v1/parcels/{id} (CONTRACT-1 shape):
 *     coverage              (bool)
 *     risk_tier             ('stable'|'watch'|'alert'|'insufficient_data')
 *     displacement_mm_yr    (float|null, signed; negative = subsiding)
 *     direction             ('subsiding'|'uplifting'|null)
 *     coherence             (float|null, 0–1)
 *     velocity_uncertainty  (float|null, mm/yr 1-sigma)
 *     source                ('OPERA L3 DISP-S1')
 *
 * Emits: ground_stability_card_view({ tier }) once per mount when a
 * numeric tier (stable/watch/alert) is present (AC-006-1 / REQ-006).
 */
function GroundStabilityCard({ groundStability }) {
  const covered = groundStability != null && groundStability.coverage !== false;
  const tier    = groundStability?.risk_tier;

  // Emit card_view once on mount when we have a real tier. This fires
  // before the disclaimer-covered tiers so the test assertion holds on
  // the initial synchronous render pass (RTL wraps render in act()).
  // Intentionally empty dep array — fires exactly once per mount.
  useEffect(() => {
    if (!groundStability) return;
    if (!covered) return;
    if (!tier || tier === 'insufficient_data') return;
    analyticsModule.trackEvent('ground_stability_card_view', { tier });
  }, []); // intentional mount-only: captures initial tier value before any re-render

  // Null guard — never render an empty card.
  if (!groundStability) return null;

  // ── No-coverage state (AC-004-1, AC-004-3) ──────────────────────
  // Covers eastern-US parcels outside the OPERA mosaic footprint.
  // Renders an informational message; never shows a Stable badge or
  // any numeric tier that would imply data exists (RISK-007 mitigation).
  if (!covered) {
    return (
      <div
        className="detail-card"
        data-testid="ground-stability-card"
        role="region"
        aria-label="Ground Stability"
      >
        <h3 style={{ margin: '0 0 8px 0' }}>Ground Stability</h3>
        <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
          No InSAR coverage in this region yet. Coverage is expanding as
          new OPERA satellite passes are processed (see{' '}
          <a
            href="https://github.com/zazula/land-ai/issues/748"
            target="_blank"
            rel="noopener noreferrer"
          >
            #748
          </a>
          ).
        </p>
        <p style={{ fontSize: '0.75rem', color: '#999', margin: '8px 0 0 0' }}>
          Source:{' '}
          <a href={OPERA_LINK} target="_blank" rel="noopener noreferrer">
            OPERA L3 DISP-S1
          </a>
        </p>
      </div>
    );
  }

  // ── Insufficient-data state (AC-004-2) ──────────────────────────
  // Covers parcels whose cell has low InSAR coherence (< 0.3).
  // Renders a brief explanation; never a numeric tier (no false precision
  // — OPP-3 constraint and AC-004-2 requirement).
  if (tier === 'insufficient_data') {
    return (
      <div
        className="detail-card"
        data-testid="ground-stability-card"
        role="region"
        aria-label="Ground Stability"
      >
        <h3 style={{ margin: '0 0 8px 0' }}>Ground Stability</h3>
        <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
          Insufficient data — coherence below threshold for a reliable
          measurement. This typically occurs in areas with dense vegetation,
          recent land cover change, or steep terrain.
        </p>
        <p style={{ fontSize: '0.75rem', color: '#999', margin: '8px 0 0 0' }}>
          Source:{' '}
          <a href={OPERA_LINK} target="_blank" rel="noopener noreferrer">
            OPERA L3 DISP-S1
          </a>
        </p>
      </div>
    );
  }

  // ── Normal tier state (stable / watch / alert) ───────────────────
  const config = TIER_CONFIG[tier] || TIER_CONFIG.stable;
  const {
    displacement_mm_yr: velocity,
    direction,
    coherence,
    velocity_uncertainty: uncertainty,
  } = groundStability;

  // Format the signed velocity string; guard against null.
  const signedVelocity =
    velocity != null
      ? `${velocity >= 0 ? '+' : ''}${velocity.toFixed(1)} mm/yr`
      : '—';

  const directionLabel =
    direction === 'subsiding'
      ? 'Subsiding'
      : direction === 'uplifting'
      ? 'Uplifting'
      : null;

  const coherencePct = coherence != null ? Math.round(coherence * 100) : null;

  // ── Ground-motion gauge geometry ────────────────────────────────
  // A diverging subsidence↔uplift scale so the signed velocity reads
  // visually, not just as a number (issue #1044 — "inSAR rendering on
  // the detail page"). The scale saturates at ±GAUGE_SCALE mm/yr; the
  // marker clamps to the ends so an extreme value (e.g. -15 mm/yr) sits
  // hard-left rather than overflowing the track. 0 = center. Negative
  // (subsidence) fills the red left half; positive (uplift) the blue
  // right half. The track carries text end-labels so it never relies on
  // color alone (WCAG 1.4.1, consistent with the tier badge).
  const GAUGE_SCALE = 15; // mm/yr at each end of the track
  const markerPct =
    velocity != null
      ? Math.max(
          0,
          Math.min(
            100,
            50 +
              (Math.max(-GAUGE_SCALE, Math.min(GAUGE_SCALE, velocity)) /
                GAUGE_SCALE) *
                50,
          ),
        )
      : null;

  return (
    <div
      className="detail-card"
      data-testid="ground-stability-card"
      role="region"
      aria-label="Ground Stability"
    >
      {/* ── Header row: title + tier badge ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Ground Stability</h3>
        <span
          data-testid="gs-tier-badge"
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            background: config.bg,
            color: config.color,
            border: `1px solid ${config.border}`,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: 0,
          }}
        >
          {config.label}
        </span>
      </div>

      {/* ── Metric grid: velocity · direction · coherence · uncertainty ── */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 2 }}>
            Surface velocity
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 600 }}>{signedVelocity}</div>
          {directionLabel && (
            <div style={{ fontSize: '0.78rem', color: '#555', marginTop: 1 }}>
              {directionLabel}
            </div>
          )}
        </div>

        {coherencePct != null && (
          <div>
            <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 2 }}>
              Coherence
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>
              {coherencePct}%
            </div>
          </div>
        )}

        {uncertainty != null && (
          <div>
            <div style={{ fontSize: '0.7rem', color: '#999', marginBottom: 2 }}>
              Uncertainty (1σ)
            </div>
            <div style={{ fontSize: '0.9rem' }}>±{uncertainty.toFixed(1)} mm/yr</div>
          </div>
        )}
      </div>

      {/* ── Ground-motion gauge (issue #1044) ──────────────────────
           Renders the signed surface velocity on a diverging
           subsidence↔uplift track so the direction + magnitude read at
           a glance. Only shown when a numeric velocity is present (it
           always is in the tier branch, but guard defensively). */}
      {markerPct != null && (
        <div data-testid="gs-motion-gauge" style={{ margin: '4px 0 12px 0' }}>
          <div
            style={{
              position: 'relative',
              height: 10,
              borderRadius: 5,
              // Diverging ramp: subsidence red → neutral → uplift blue.
              background:
                'linear-gradient(90deg, #c62828 0%, #ef9a9a 30%, #eeeeee 50%, #90caf9 70%, #1565c0 100%)',
            }}
          >
            {/* Center (0 mm/yr) tick */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '50%',
                top: -2,
                width: 1,
                height: 14,
                background: 'rgba(0,0,0,0.35)',
                transform: 'translateX(-0.5px)',
              }}
            />
            {/* Marker for this parcel's velocity */}
            <div
              data-testid="gs-motion-marker"
              title={signedVelocity}
              style={{
                position: 'absolute',
                left: `${markerPct}%`,
                top: -3,
                width: 12,
                height: 16,
                transform: 'translateX(-6px)',
                borderRadius: 3,
                background: '#fff',
                border: '2px solid #333',
                boxShadow: '0 0 2px rgba(0,0,0,0.4)',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.66rem',
              color: '#999',
              marginTop: 4,
            }}
          >
            <span>Subsidence −{GAUGE_SCALE}</span>
            <span>0 mm/yr</span>
            <span>Uplift +{GAUGE_SCALE}</span>
          </div>
        </div>
      )}

      {/* ── Methodology note + OPERA source link (tooltip-like) ── */}
      <p style={{ fontSize: '0.73rem', color: '#888', margin: '0 0 6px 0' }}>
        Derived from{' '}
        <a
          href={OPERA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          title="NASA/JPL OPERA L3 DISP-S1 — Sentinel-1 surface displacement product"
        >
          OPERA L3 DISP-S1
        </a>{' '}
        (Sentinel-1 SAR interferometry). Negative velocity = land subsidence;
        positive = uplift.
      </p>

      {/* ── MANDATORY non-dismissible disclaimer (AC-003-2 / RISK-010) ──
           Must be present whenever a tier renders; never behind a hover. */}
      <div
        data-testid="gs-disclaimer"
        style={{
          marginTop: 8,
          padding: '6px 10px',
          background: '#f5f5f5',
          borderLeft: '3px solid #bdbdbd',
          fontSize: '0.73rem',
          color: '#666',
          lineHeight: 1.45,
        }}
      >
        {DISCLAIMER}
      </div>
    </div>
  );
}

export default GroundStabilityCard;
